/**
 * Lee variables VITE_* del build o del runtime inyectado por Cloudflare Worker
 * (`window.__EDGE_RUNTIME_CONFIG__` / `window.__BRAND_RUNTIME_CONFIG__`).
 */

import { coalesceMuxPlaybackId } from "@/lib/canonicalMuxPlayback";
import { getEdgeRuntimeConfig } from "@/lib/edgeRuntimeConfig";

declare global {
  interface Window {
    __BRAND_RUNTIME_CONFIG__?: Record<string, string | undefined>;
  }
}

const EDGE_KEY_MAP: Record<string, string> = {
  VITE_BRAND_SOURCE_KEY: "brandSourceKey",
  VITE_CLERK_PUBLISHABLE_KEY: "clerkPublishableKey",
  VITE_STRIPE_PUBLISHABLE_KEY: "stripePublishableKey",
  VITE_CLOUDINARY_CLOUD_NAME: "cloudinaryCloudName",
  VITE_CLOUDINARY_UPLOAD_PRESET: "cloudinaryUploadPreset",
  VITE_CLOUDINARY_BASE_FOLDER: "cloudinaryBaseFolder",
  VITE_CLOUDINARY_WATERMARK_ID: "cloudinaryWatermarkId",
  VITE_POSTHOG_KEY: "posthogKey",
  VITE_POSTHOG_HOST: "posthogHost",
  VITE_PREMIUM_MUX_ES_VIP: "premiumMuxEsVip",
  VITE_PREMIUM_MUX_EN_VIP: "premiumMuxEnVip",
  VITE_MUX_CTA_UNIVERSAL_PLAYBACK_ID: "muxCtaUniversalPlaybackId",
  VITE_TELEGRAM_GROUPS_PLAYBACK_ID: "telegramGroupsPlaybackId",
  VITE_TELEGRAM_GROUPS_FALLBACK_PLAYBACK_ID: "telegramGroupsFallbackPlaybackId",
  VITE_MP_CHECKOUT_BASIC_AR: "mpCheckoutBasicAr",
  VITE_MP_CHECKOUT_VIP_AR: "mpCheckoutVipAr",
  VITE_MP_CHECKOUT_VIP_DISCOUNT_AR: "mpCheckoutVipDiscountAr",
  VITE_MP_TELEGRAM_GROUP_LINK: "mpTelegramGroupLink",
};

/**
 * Playback IDs that must never surface (cross-brand / bad fallback).
 * Keep in sync with api/lib/muxPlaybackValidation.ts MUX_PLAYBACK_DENYLIST.
 */
const MUX_PLAYBACK_DENYLIST: ReadonlySet<string> = new Set([
  // Incidente 2026-07-13: asset que NO es de Delfina (era fallback en wrangler).
  "KcpwA0200PxCNZlNsLGSPzfe01yPcDSOAnReR02wdhf02lYs",
]);

/** Keys whose values are Mux playback IDs — prefer admin/edge over baked build. */
function isMuxPlaybackEnvKey(name: string): boolean {
  return /MUX|PLAYBACK|PREMIUM_MUX/i.test(name);
}

/** MP checkout URLs — prefer admin DB via brand-runtime over wrangler/build. */
function isMpCheckoutEnvKey(name: string): boolean {
  return (
    name === "VITE_MP_CHECKOUT_BASIC_AR" ||
    name === "VITE_MP_CHECKOUT_VIP_AR" ||
    name === "VITE_MP_CHECKOUT_VIP_DISCOUNT_AR" ||
    name === "VITE_MP_TELEGRAM_GROUP_LINK"
  );
}

function isDeniedMuxPlaybackId(id: string): boolean {
  return MUX_PLAYBACK_DENYLIST.has(id);
}

/**
 * Strip denylisted Mux IDs (raw id or stream.mux.com URL). Non-denied values pass through.
 */
function stripDeniedMuxValue(raw: string): string {
  if (!raw) return "";
  const t = raw.trim();
  if (!t) return "";
  if (isDeniedMuxPlaybackId(t)) return "";
  const muxUrlMatch = t.match(/stream\.mux\.com\/([^/.?]+)/);
  if (muxUrlMatch && isDeniedMuxPlaybackId(muxUrlMatch[1])) return "";
  return t;
}

function fromImportMeta(name: string): string {
  const raw = (import.meta.env as Record<string, string | undefined>)[name];
  return typeof raw === "string" ? raw.trim() : "";
}

function fromEdge(name: string): string {
  const edgeKey = EDGE_KEY_MAP[name];
  if (!edgeKey) return "";
  const cfg = getEdgeRuntimeConfig();
  const v = cfg?.[edgeKey as keyof typeof cfg];
  return typeof v === "string" ? v.trim() : "";
}

function fromBrandRuntime(name: string): string {
  if (typeof window === "undefined") return "";
  const cfg = window.__BRAND_RUNTIME_CONFIG__;
  if (!cfg || typeof cfg !== "object") return "";
  const v = cfg[name];
  return typeof v === "string" ? v.trim() : "";
}

/**
 * Primera fuente no vacía.
 * Default: build Vite → edge HTML → fetch API brand-runtime.
 * Mux/playback keys: edge → brand-runtime → import.meta (admin-driven Worker wins;
 * also avoids denylisted IDs baked into old CF Pages builds).
 * MP checkout keys: brand-runtime (mercadopago_plans admin) → edge → import.meta.
 * Denylisted Mux IDs are stripped from every source.
 */
export function readRuntimeBrandEnv(name: string): string {
  const muxKey = isMuxPlaybackEnvKey(name);
  const mpKey = isMpCheckoutEnvKey(name);
  const sources = muxKey
    ? [fromEdge(name), fromBrandRuntime(name), fromImportMeta(name)]
    : mpKey
      ? [fromBrandRuntime(name), fromEdge(name), fromImportMeta(name)]
      : [fromImportMeta(name), fromEdge(name), fromBrandRuntime(name)];

  for (const raw of sources) {
    if (!raw) continue;
    if (muxKey) {
      const cleaned = stripDeniedMuxValue(raw);
      if (cleaned) return cleaned;
      continue;
    }
    return raw;
  }
  return "";
}

export function parseMuxPlaybackId(raw: string | undefined): string {
  if (!raw?.trim()) return "";
  let t = raw.trim();
  const muxUrlMatch = t.match(/stream\.mux\.com\/([^/.?]+)/);
  if (muxUrlMatch) t = muxUrlMatch[1];
  if (t.length < 10 || /your-|tu-|example|xxx/i.test(t)) return "";
  if (isDeniedMuxPlaybackId(t)) return "";
  return t;
}

/** Cadena de playback para funnel Mux+CTA cuando el build CF Pages no trae VITE_*. */
export function resolveMuxCtaPlaybackFallback(countryCode?: string | null): string {
  const cc = (countryCode || "US").toUpperCase();
  const universal = parseMuxPlaybackId(
    readRuntimeBrandEnv("VITE_MUX_CTA_UNIVERSAL_PLAYBACK_ID"),
  );
  const esVip = parseMuxPlaybackId(readRuntimeBrandEnv("VITE_PREMIUM_MUX_ES_VIP"));
  const enVip = parseMuxPlaybackId(readRuntimeBrandEnv("VITE_PREMIUM_MUX_EN_VIP"));
  const telegram = parseMuxPlaybackId(
    readRuntimeBrandEnv("VITE_TELEGRAM_GROUPS_PLAYBACK_ID"),
  );
  const telegramFallback = parseMuxPlaybackId(
    readRuntimeBrandEnv("VITE_TELEGRAM_GROUPS_FALLBACK_PLAYBACK_ID"),
  );

  const spanishCountries = new Set([
    "ES", "MX", "AR", "CO", "PE", "CL", "VE", "EC", "BO", "PY", "UY",
    "CR", "PA", "DO", "GT", "HN", "NI", "SV", "CU", "PR", "LATAM",
  ]);

  if (cc === "AR" && telegram) return telegram;
  if (spanishCountries.has(cc)) {
    return coalesceMuxPlaybackId(
      esVip || telegram || universal || telegramFallback || enVip,
    );
  }
  return coalesceMuxPlaybackId(
    enVip || esVip || universal || telegram || telegramFallback,
  );
}
